import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import AssignmentIcon from "@/assets/icons/assignment.svg?react";
import { useGetScreeningReportHtmlQuery } from "@/features/patients/infrastructure/api/patientsApi";
import { textStyles } from "@/lib/textStyles";
import { mapScreeningReportHtml } from "@/features/patients/infrastructure/api/mappers/screeningReportMapper";
import ScreeningReportView from "./ScreeningReportView";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  REPORT_GENERATING_STATUS,
  REPORT_PENDING_STATUSES,
  REPORT_READY_STATUSES,
} from "@/features/patients/infrastructure/data/reportGeneration";

interface Props {
  patientId: string;
  assessmentId: string;
  status: string;
}

const ScreeningReportSection: React.FC<Props> = ({
  patientId,
  assessmentId,
  status,
}) => {
  const { t, i18n } = useTranslation("patients");
  const shouldFetchReport = REPORT_READY_STATUSES.has(status);
  const [reportLoading, setReportLoading] = useState(false);
  const { data, isLoading, isError } = useGetScreeningReportHtmlQuery(
    { patientId, assessmentId },
    { skip: !shouldFetchReport || !patientId || !assessmentId },
  );

  const reportHtml = data?.html;
  const report = useMemo(
    () => (reportHtml ? mapScreeningReportHtml(reportHtml) : null),
    [reportHtml],
  );

  const downloadReport = async () => {
    if (!report) return;
    setReportLoading(true);
    try {
      // Loaded on demand so jsPDF stays out of the main bundle.
      const { buildScreeningReportPdf, screeningReportFileName } = await import(
        "@/features/patients/infrastructure/pdf/screeningReportPdf"
      );
      // PDF fonts only cover Latin script, so the document is always English.
      const doc = buildScreeningReportPdf(report, i18n.getFixedT("en", "patients"));
      doc.save(screeningReportFileName(assessmentId));
    } catch (error) {
      console.error(error);
      toast.error(t("reportSection.downloadError"));
    } finally {
      setReportLoading(false);
    }
  };

  const isReportPending = REPORT_PENDING_STATUSES.has(status);
  const isReportGenerating = status === REPORT_GENERATING_STATUS;

  const showPlaceholder =
    !shouldFetchReport ||
    status === "SCR_SCHEDULED" ||
    isReportPending ||
    isReportGenerating ||
    status === "SCR_REPORT_GENERATION_FAILED";

  return (
    <Card className="mx-auto mt-6 gap-3 bg-background px-4 text-center sm:px-5">
      <div className="overflow-hidden">
        <div className="flex flex-col gap-2">
          <div className="flex w-full flex-col items-stretch gap-3 text-left sm:flex-row sm:items-center sm:justify-between">
            <h3 style={textStyles.heading3}>{t("reportSection.title")}</h3>
            <div className="flex w-full justify-end sm:ml-auto sm:w-auto">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="block w-full sm:w-auto">
                    <Button
                      variant="outline"
                      className="w-full border-primary bg-white text-primary hover:bg-white hover:text-primary cursor-pointer sm:w-auto"
                      disabled={!shouldFetchReport || !report || reportLoading}
                      onClick={() => void downloadReport()}
                    >
                      {reportLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t("reportSection.downloading")}
                        </>
                      ) : (
                        t("reportSection.downloadPdf")
                      )}
                    </Button>
                  </span>
                </TooltipTrigger>
                {!shouldFetchReport && (
                  <TooltipContent>
                    {t("reportSection.pdfAvailableHint")}
                  </TooltipContent>
                )}
              </Tooltip>
            </div>
          </div>

          <Separator />
        </div>
      </div>

      {showPlaceholder && (
        <div className="flex min-h-[320px] flex-col items-center justify-center gap-2 px-1 py-8 sm:min-h-[400px]">
          <AssignmentIcon />
          <h4 className="text-lg font-semibold">{t("reportSection.placeholderTitle")}</h4>
          <p className="w-full max-w-md text-gray-500">
            {isReportPending
              ? t("reportSection.placeholderPending")
              : isReportGenerating
                ? t("reportSection.placeholderGenerating")
                : t("reportSection.placeholderDefault")}
          </p>
        </div>
      )}

      {shouldFetchReport && isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      )}

      {shouldFetchReport && isError && (
        <p className="text-sm text-red-600">
          {t("reportSection.loadError")}
        </p>
      )}

      {shouldFetchReport && report && <ScreeningReportView report={report} />}

      {/* Fallback: report HTML we can't map yet — render it as delivered */}
      {shouldFetchReport && data?.html && !report && (
        <div
          className="w-full max-w-full overflow-x-auto bg-white text-left [&_img]:h-auto [&_img]:max-w-full"
          dangerouslySetInnerHTML={{ __html: data.html }}
        />
      )}
    </Card>
  );
};

export default ScreeningReportSection;
